import { useState } from "react";
import { Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@visioni/a3s-ui/components";
import { Icon } from "@visioni/a3s-ui/components";
import type { IconName } from "@visioni/a3s-ui/icons";
import { differenceInCalendarDays, format, formatDistanceToNow, isYesterday, isToday } from "date-fns";
import type { ActivityKind, ActivityStep, ActivityStepStatus } from "./types";

export interface NotificationItemProps {
  id: string;
  kind: ActivityKind;
  summary: string;
  status?: ActivityStepStatus;
  createdAt: string;
  read: boolean;
  subtitle?: string;
  onPress?: () => void;
  onNavigate?: () => void;
  isLast?: boolean;
  steps?: readonly ActivityStep[];
  author?: string;
}

const ICON_FOR_KIND: Record<ActivityKind, IconName> = {
  alarm_activated: "bell",
  alarm_routine_configured: "calendarRefresh",
  alarm_response_configured: "alarm",
  alarm_exception_configured: "calendarEvent",
  alarm_timezone_configured: "clock",
  alarm_override_configured: "alarmPause",
  alarm_silence_configured: "silence",
  alarm_contacts_configured: "personShield",
  site_alarm_override_configured: "alarmPause",
  user_permission_granted: "bell",
  user_permission_revoked: "bell",
  livestream_viewed: "watchLive",
  clip_viewed: "watchLive",
  clip_compilation_requested: "videoRecording",
  clip_compilation_viewed: "watchLive",
  test_signal_triggered: "activity",
  deterrence_triggered: "siren",
  deterrence_stopped: "silence",
};

const STATUS_ICON: Record<ActivityStepStatus, IconName> = {
  completed: "checkmark",
  in_progress: "refresh",
  failed: "error",
  pending: "dot",
};

const STATUS_COLOR: Record<ActivityStepStatus, (theme: ReturnType<typeof useUnistyles>["theme"]) => string> = {
  completed: (theme) => theme.colors.status.success,
  in_progress: (theme) => theme.colors.blue[500],
  failed: (theme) => theme.colors.status.error,
  pending: (theme) => theme.colors.textMuted,
};

function StepRow({ step, isLast }: { step: ActivityStep; isLast: boolean }) {
  const { theme } = useUnistyles();

  return (
    <View flexDirection="row" alignItems="flex-start" gap="md">
      <View alignItems="center" width={16}>
        <Icon name={STATUS_ICON[step.status]} size={14} color={STATUS_COLOR[step.status](theme)} />
        {!isLast && <View width={1} height={14} style={{ backgroundColor: theme.colors.borderCard }} />}
      </View>
      <View
        flex={1}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        {...(!isLast && { pb: "sm" as const })}
      >
        <Text textStyle="xxs/regular" color="textMuted">
          {step.label}
        </Text>
        {step.timestamp && (
          <Text textStyle="xxs/regular" color="textMuted" style={{ opacity: 0.7 }}>
            {format(new Date(step.timestamp), "d MMM, h:mm a")}
          </Text>
        )}
      </View>
    </View>
  );
}

function formatNotificationTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const daysDiff = differenceInCalendarDays(new Date(), date);
  let timeLabel: string;
  if (isToday(date)) {
    timeLabel = formatDistanceToNow(date, { addSuffix: true });
  } else if (isYesterday(date)) {
    timeLabel = `Yesterday at ${format(date, "HH:mm")}`;
  } else if (daysDiff < 7) {
    timeLabel = format(date, "EEEE 'at' HH:mm");
  } else {
    timeLabel = format(date, "d MMM yyyy");
  }
  return timeLabel;
}

export function NotificationItem({
  kind,
  summary,
  status,
  createdAt,
  read,
  subtitle,
  onPress,
  onNavigate,
  isLast = false,
  steps,
  author,
}: NotificationItemProps) {
  const { theme } = useUnistyles();
  const [expanded, setExpanded] = useState(false);

  const iconName = ICON_FOR_KIND[kind] ?? "activity";
  const expandable = steps && steps.length > 0;
  const timeLabel = formatNotificationTimestamp(createdAt);

  function handlePress() {
    if (expandable) {
      setExpanded((prev) => !prev);
    }
    onPress?.();
  }

  return (
    <Pressable onPress={handlePress}>
      <View
        flexDirection="row"
        alignItems="flex-start"
        py="xl"
        gap="lg"
        borderBottomWidth={isLast ? 0 : 1}
        borderColor="borderCard"
      >
        <View
          width={32}
          height={32}
          borderRadius={16}
          alignItems="center"
          justifyContent="center"
          style={{ opacity: read ? 0.6 : 1, backgroundColor: theme.colors.blue[800] }}
        >
          <Icon name={iconName} size={16} color={read ? theme.colors.textMuted : theme.colors.white.default} />
        </View>

        <View flex={1} gap="xs">
          <View flexDirection="row" justifyContent="space-between" alignItems="center">
            <View flexDirection="row" alignItems="center" gap="sm" style={{ flex: 1 }}>
              <Text
                textStyle={read ? "xs/regular" : "xs/semibold"}
                color="text"
                numberOfLines={expanded ? undefined : 2}
                style={{ flex: 1 }}
              >
                {summary}
              </Text>
              {status && <Icon name={STATUS_ICON[status]} size={14} color={STATUS_COLOR[status](theme)} />}
            </View>
          </View>

          {subtitle && (
            <Pressable onPress={onNavigate} disabled={!onNavigate} style={{ alignSelf: "flex-start" }}>
              <View flexDirection="row" alignItems="center" gap="xs">
                <Text
                  textStyle="xxs/medium"
                  color={onNavigate ? "blue" : "textMuted"}
                  numberOfLines={1}
                  style={onNavigate ? { textDecorationLine: "underline" } : undefined}
                >
                  {subtitle}
                </Text>
              </View>
            </Pressable>
          )}

          <View flexDirection="row" alignItems="center" gap="sm">
            <Text textStyle="xxs/regular" color="textMuted" style={{ opacity: 0.7 }}>
              {timeLabel}
            </Text>
            {expandable && (
              <Icon name={expanded ? "chevronUp" : "chevronDown"} size={12} color={theme.colors.textMuted} />
            )}
            {author && (
              <View flexDirection="row" alignItems="center" gap="xs" style={{ marginLeft: "auto" }}>
                <Icon name="user" size={11} color={theme.colors.textMuted} />
                <Text textStyle="xxs/regular" color="textMuted">
                  {author}
                </Text>
              </View>
            )}
          </View>

          {expanded && steps && (
            <View mt="md" ml="xs" gap="xs">
              {steps.map((step, i) => (
                <StepRow key={i} step={step} isLast={i === steps.length - 1} />
              ))}
            </View>
          )}
        </View>
      </View>
    </Pressable>
  );
}
